import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { SupabaseAuthProvider as AuthProvider, useAuth } from './contexts/SupabaseAuthContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { DemoProvider, useDemo } from './contexts/DemoContext';
import ClassroomProvider from './contexts/ClassroomContext';
import { Header } from './components/Header';
import { AuthModal } from './components/AuthModal';
import { SessionManager } from './components/auth/SessionManager';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { getEnvironmentInfo, validateEnvironment } from './lib/config';
import { lazyWithPreload, preloadOnIdle } from './utils/lazyWithPreload';
import { useImagePreloader, useDemoAvatarPreloader } from './hooks/useImagePreloader';
import { deploymentConfig } from './force-rebuild';

// Enhanced lazy loading with preload capabilities
const HomePage = lazyWithPreload(() => import('./pages/HomePage').then(module => ({ default: module.HomePage })));
const EditorPage = lazyWithPreload(() => import('./pages/EditorPage').then(module => ({ default: module.EditorPage })));
const GalleryPage = lazyWithPreload(() => import('./pages/GalleryPage').then(module => ({ default: module.GalleryPage })));
const LearnPage = lazyWithPreload(() => import('./pages/LearnPage').then(module => ({ default: module.LearnPage })));
const SampleProjectsPage = lazyWithPreload(() => import('./pages/SampleProjectsPage').then(module => ({ default: module.SampleProjectsPage })));
const DashboardPage = lazyWithPreload(() => import('./pages/DashboardPage'));
const AuthCallbackPage = lazyWithPreload(() => import('./pages/AuthCallbackPage').then(module => ({ default: module.AuthCallbackPage })));
const RoleSelectionPage = lazyWithPreload(() => import('./pages/RoleSelectionPage').then(module => ({ default: module.RoleSelectionPage })));

// Lazy load auxiliary components
const DemoUserSwitcher = lazyWithPreload(() => import('./components/DemoUserSwitcher').then(module => ({ default: module.DemoUserSwitcher })));
const DemoModeIndicator = lazyWithPreload(() => import('./components/DemoModeIndicator').then(module => ({ default: module.DemoModeIndicator })));
const NovaChat = lazyWithPreload(() => import('./components/assistant/NovaChat').then(module => ({ default: module.NovaChat })));

// Temporary test components
const HuggingFaceTest = lazyWithPreload(() => import('./components/HuggingFaceTest'));
const SupabaseConnectionTest = lazyWithPreload(() => import('./components/SupabaseConnectionTest').then(module => ({ default: module.SupabaseConnectionTest })));
const AuthTestPage = lazyWithPreload(() => import('./components/AuthTestPage').then(module => ({ default: module.AuthTestPage })));

// Loading component for lazy-loaded routes
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

const AppContent: React.FC = () => {
  const { isAuthModalOpen, hideAuthModal } = useAuth();
  const { isDemo } = useDemo();

  // Preload images based on current route
  useImagePreloader();
  
  // Preload demo avatars when in demo mode
  useDemoAvatarPreloader(isDemo);

  // Preload commonly accessed components when app starts
  useEffect(() => {
    // Preload critical components when browser is idle
    preloadOnIdle(DashboardPage);
    preloadOnIdle(EditorPage);
    preloadOnIdle(NovaChat);
    
    // Note: Blockly preloading is handled in individual components
  }, []);

  return (
    <>
      <Header />
      <main className="grow">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/editor" element={
              <ProtectedRoute>
                <EditorPage />
              </ProtectedRoute>
            } />
            <Route path="/editor/:projectId" element={
              <ProtectedRoute>
                <EditorPage />
              </ProtectedRoute>
            } />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/learn" element={<LearnPage />} />
            <Route path="/learn/:topic" element={<LearnPage />} />
            <Route path="/samples" element={<SampleProjectsPage />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/onboarding/role-selection" element={<RoleSelectionPage />} />
            <Route path="/test-hf" element={<HuggingFaceTest />} />
            <Route path="/test-supabase" element={<SupabaseConnectionTest />} />
            <Route path="/test-auth" element={<AuthTestPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
      <AuthModal isOpen={isAuthModalOpen} onClose={hideAuthModal} />
      
      {/* Demo Mode Components - Lazy loaded */}
      <Suspense fallback={null}>
        <DemoUserSwitcher position="bottom-left" />
        <DemoModeIndicator />
      </Suspense>
      
      {/* Nova AI Assistant - Lazy loaded */}
      <Suspense fallback={null}>
        <NovaChat position="bottom-right" />
      </Suspense>
    </>
  );
};

function App() {
  // Log environment information on app start
  useEffect(() => {
    const envInfo = getEnvironmentInfo();
    const validation = validateEnvironment();
    
    console.log('🌍 CraftyJoy Environment:', envInfo.environment);
    console.log('🔄 Deployment Config:', deploymentConfig);
    
    if (!validation.isValid) {
      console.error('🚨 Environment validation failed:', validation.errors);
    }
    
    // Show environment info
    console.log(`🎭 Demo mode enabled for all environments`);
    if (envInfo.environment === 'development') {
      console.log('🔧 Development mode');
      console.log('📋 Environment details:', envInfo);
    } else {
      console.log('🚀 Production mode with demo features enabled');
    }
  }, []);

  return (
    <Router>
      <AuthProvider>
        <SessionManager onSessionExpired={() => console.log('Session expired')}>
          <DemoProvider>
            <ProjectProvider>
              <ClassroomProvider>
                <div className="flex flex-col min-h-screen bg-gray-50">
                  <AppContent />
                </div>
              </ClassroomProvider>
            </ProjectProvider>
          </DemoProvider>
        </SessionManager>
      </AuthProvider>
    </Router>
  );
}

export default App;